import { useCallback, type MutableRefObject, type RefObject } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines';
import { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
import type { SchemaSnapshot } from '../../../sql-resolver/types';
import { notifyAddToWhereFailed, type AddToWhereFailure } from '../../common/hooks/useCellFilterDraftRequest';
import { useCellFilterDraftStore, type CellFilterDraftRequest } from '../../common/stores/cellFilterDraftStore';
import { planAddToWhere, type AddToWherePlan } from '../../common/utils/addToWhere';
import type { ResultColumnTarget } from '../../common/utils/resultColumnWhereField';
import { hoverSchemaSnapshot } from '../../common/utils/selectStarHoverProvider';
import { VQE_DOC_PREFIX } from '../../visual-query-editor/mode/QueryVisualModePanel';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import type { QueryEditorHandle } from '../components/QueryEditor';

/** How long a draft request (Visual view, or the SQL view's compact popover) waits for a WHERE builder to take it. */
export const UNTAKEN_DRAFT_MS = 5000;

/**
 * Post a draft request for a WHERE builder. One no builder took within
 * `UNTAKEN_DRAFT_MS` (none can edit the statement) is dropped with a toast
 * saying so, rather than opening a stale draft whenever one mounts.
 */
export function postDraftRequest(request: CellFilterDraftRequest, untaken: Exclude<AddToWhereFailure, 'field'>): void {
  useCellFilterDraftStore.getState().requestDraft(request);
  setTimeout(() => {
    if (useCellFilterDraftStore.getState().take(request)) notifyAddToWhereFailed(request.column.name, untaken);
  }, UNTAKEN_DRAFT_MS);
}

type UseResultsAddToWhereParams = {
  tabId: string;
  engineId: DatabaseEngineId;
  connectionId: string | undefined;
  database: string | undefined;
  /** The tab shows its Visual view: open a condition draft there instead of editing text. */
  isVisual: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
  /** Partial-run tracking ref owned by the host (kept in sync by executions). */
  lastPartialExecutedSqlRef: MutableRefObject<string | null>;
};

/**
 * The results grid's "Add to WHERE" for a Query tab.
 *
 * Visual view: the cell goes to the view's WHERE condition builder, which
 * opens a pre-filled draft (`useCellFilterDraftRequest`) — a drilled subquery
 * is left first, since the result belongs to the whole statement.
 *
 * SQL view: the condition is written into the statement (`planAddToWhere`).
 * Scope resolution priority:
 *  1. Active text selection — modify just the selected text.
 *  2. Last partial execution — when the active result set came from running a
 *     single statement out of many (cursor-statement bar, selection bar, F5 on
 *     a cursor statement), modify ONLY that statement inside the buffer so
 *     sibling statements are left untouched. Requires the executed text to
 *     still be present verbatim in the buffer; if the user has edited it
 *     since, fall through to (3).
 *  3. Full buffer — the buffer's last statement.
 * When the statement reads several sources and nothing says which one the
 * column comes from (SQLite reports no provenance), the schema snapshot places
 * it; the edit then waits for that snapshot.
 */
export function useResultsAddToWhere({
  tabId,
  engineId,
  connectionId,
  database,
  isVisual,
  editorRef,
  lastPartialExecutedSqlRef,
}: UseResultsAddToWhereParams) {
  const { track } = useAnalytics();

  const applyInSqlView = useCallback(
    (column: ResultColumnTarget, value: unknown, snapshot?: SchemaSnapshot | null): AddToWherePlan['kind'] => {
      const editor = editorRef.current;
      if (!editor) return 'edit';
      const plan = (text: string) => planAddToWhere(text, column, value, engineId, snapshot);
      let applied: AddToWherePlan;

      const selectedText = editor.getSelectedText();
      const selection = selectedText ? editor.getSelectionRange() : null;
      const fullBuffer = editor.getValue() ?? '';
      const partialSql = lastPartialExecutedSqlRef.current;
      const partialIdx = !selectedText && partialSql ? fullBuffer.indexOf(partialSql) : -1;

      if (selectedText) {
        applied = plan(selectedText);
        if (applied.kind !== 'edit') return applied.kind;
        // A plain edit over the selection: a snippet would read `$1` / `\` in a value as snippet syntax.
        if (!(selection && editor.executeEdits?.('add-to-where', [{ range: selection, text: applied.text }]))) {
          editor.insertSnippet(applied.text.replace(/[\\$}]/g, '\\$&'));
        }
        const lineCount = (editor.getValue() ?? '').split('\n').length;
        editor.revealLine(lineCount);
      } else if (partialSql && partialIdx >= 0) {
        applied = plan(partialSql);
        if (applied.kind !== 'edit') return applied.kind;
        const modified = applied.text;
        editor.setValue(fullBuffer.slice(0, partialIdx) + modified + fullBuffer.slice(partialIdx + partialSql.length));
        // Reveal the line where the modified statement ends so the user sees
        // the new condition without being teleported to the end of the file.
        editor.revealLine((fullBuffer.slice(0, partialIdx) + modified).split('\n').length);
        // Keep the ref in sync with the new text so a follow-up Add-to-WHERE
        // continues refining the same statement.
        lastPartialExecutedSqlRef.current = modified;
      } else {
        applied = plan(fullBuffer);
        if (applied.kind !== 'edit') return applied.kind;
        editor.setValue(applied.text);
        editor.revealLine(applied.text.split('\n').length);
      }
      editor.focus();
      track('feature:qe_add_to_where', { engine: engineId, view: 'sql', operator: applied.operator });
      return 'edit';
    },
    [editorRef, engineId, lastPartialExecutedSqlRef, track],
  );

  return useCallback(
    (_columnName: string, value: unknown, column: ResultColumnTarget) => {
      if (isVisual) {
        useVqeStore.getState().clearDrill(tabId);
        postDraftRequest({ documentId: `${VQE_DOC_PREFIX}${tabId}`, column, value }, 'not-editable');
        return;
      }
      const first = applyInSqlView(column, value);
      if (first === 'edit') return;
      const sqlEngine = databaseEngineIdToSqlEngine(engineId);
      // Only an unplaced column can be placed by the schema snapshot.
      if (first !== 'unknown' || !connectionId || !database || !sqlEngine) {
        notifyAddToWhereFailed(column.name, first);
        return;
      }
      void hoverSchemaSnapshot(connectionId, sqlEngine, database)
        .catch(() => undefined)
        .then((snapshot) => {
          const second = snapshot ? applyInSqlView(column, value, snapshot) : first;
          if (second !== 'edit') notifyAddToWhereFailed(column.name, second);
        });
    },
    [isVisual, tabId, applyInSqlView, engineId, connectionId, database],
  );
}
